import { useMemo, useState } from 'react';
import { KeyRound, Plus } from 'lucide-react';
import { DeviceTile } from '@/components/devices/DeviceTile';
import { DevicePanel } from '@/components/devices/DevicePanel';
import { FacilityTabs } from '@/components/facilities/FacilityTabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useAllKeys, useRevokeKey } from '@/hooks/useKeys';
import { useDevices, useRemoveDevice } from '@/hooks/useDevices';
import { useDeviceStore } from '@/stores/deviceStore';
import { groupByFacility } from '@/lib/facilities';
import type { AccessKey, DeviceAssignment } from '@/types';

interface KeyAccessListProps {
  /** Anläggningsmeny + gruppering per anläggning (Nycklar-fliken). */
  showFacilityTabs?: boolean;
  /** Tillåt borttagning av egna enheter (anläggnings-ärvda undantas). */
  allowRemove?: boolean;
  /** Anropas från tomlägets "Lägg till enhet"-knapp. Utan callback visas bara text. */
  onAdd?: () => void;
}

/**
 * KeyAccessList — delad nyckellista för Hem och Nycklar.
 *
 * Visar användarens enheter som DeviceTiles; tap öppnar DevicePanel med
 * enhetens nycklar (QR, wallet, tilldela, dela, återkalla). Nycklar-fliken
 * lägger på anläggningsmenyn (FacilityTabs) med gruppering och hantering
 * (ta bort egna enheter) — det som tidigare bodde på Enheter-fliken.
 */
export function KeyAccessList({ showFacilityTabs, allowRemove, onAdd }: KeyAccessListProps) {
  const { assignments, setActiveDevice } = useDeviceStore();
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [panelAssignment, setPanelAssignment] = useState<DeviceAssignment | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<string | null>(null);

  const { isLoading: devicesLoading } = useDevices();
  const { data: keys, isLoading: keysLoading } = useAllKeys();
  const revokeKey = useRevokeKey();
  const removeDevice = useRemoveDevice();

  const keysByDevice = useMemo(() => {
    const map = new Map<string, AccessKey[]>();
    for (const key of keys ?? []) {
      const list = map.get(key.device_id);
      if (list) list.push(key);
      else map.set(key.device_id, [key]);
    }
    return map;
  }, [keys]);

  const facilityGroups = useMemo(() => groupByFacility(assignments), [assignments]);
  const facilities = useMemo(() => facilityGroups.map((g) => g.name), [facilityGroups]);
  const visibleGroups = useMemo(
    () =>
      selectedFacility
        ? facilityGroups.filter((g) => g.name === selectedFacility)
        : facilityGroups,
    [facilityGroups, selectedFacility]
  );

  const handleRevoke = async (keyId: string) => {
    setRevokingId(keyId);
    try {
      await revokeKey.mutateAsync(keyId);
    } finally {
      setTimeout(() => setRevokingId(null), 400);
    }
  };

  const handleRemove = async (assignmentId: string) => {
    setRemovingId(assignmentId);
    try {
      await removeDevice.mutateAsync(assignmentId);
    } finally {
      setTimeout(() => setRemovingId(null), 400);
    }
  };

  const openPanel = (assignment: DeviceAssignment) => {
    if (assignment.device) setActiveDevice(assignment.device);
    setPanelAssignment(assignment);
    setPanelOpen(true);
  };

  const renderTile = (assignment: DeviceAssignment) => (
    <DeviceTile
      key={assignment.id}
      assignment={assignment}
      activeKeys={keysByDevice.get(assignment.device_id) ?? []}
      onOpen={openPanel}
      // Anläggnings-ärvda dörrar hanteras av administratören och kan
      // inte tas bort per enhet av användaren.
      onRemove={
        allowRemove && assignment.source !== 'facility' ? handleRemove : undefined
      }
      isRemoving={removingId === assignment.id}
    />
  );

  const hasDevices = assignments.length > 0;
  const isLoading = devicesLoading || keysLoading;

  if (isLoading && !hasDevices) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-[92px] w-full rounded-3xl" />
        ))}
      </div>
    );
  }

  if (!hasDevices) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl glass-thin">
          <KeyRound className="h-7 w-7 text-muted-foreground" />
        </div>
        <p className="text-lg font-medium">Inga nycklar ännu</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Lägg till en enhet med kod för att få din första nyckel
        </p>
        {onAdd && (
          <Button className="mt-4" onClick={onAdd}>
            <Plus className="mr-1.5 h-4 w-4" />
            Lägg till enhet
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showFacilityTabs && (
        <FacilityTabs
          facilities={facilities}
          selected={selectedFacility}
          onSelect={setSelectedFacility}
        />
      )}

      {showFacilityTabs ? (
        visibleGroups.map((group) => (
          <section key={group.name} className="space-y-3 animate-fade-in">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold">{group.name}</h3>
              <span className="text-xs text-muted-foreground">
                {group.assignments.length}{' '}
                {group.assignments.length === 1 ? 'enhet' : 'enheter'}
              </span>
            </div>
            <div className="space-y-3">{group.assignments.map(renderTile)}</div>
          </section>
        ))
      ) : (
        <section className="space-y-3 animate-fade-in">{assignments.map(renderTile)}</section>
      )}

      <DevicePanel
        assignment={panelAssignment}
        keys={panelAssignment ? (keysByDevice.get(panelAssignment.device_id) ?? []) : []}
        open={panelOpen}
        onOpenChange={setPanelOpen}
        onRevoke={handleRevoke}
        revokingId={revokingId}
      />
    </div>
  );
}
